import { useState, FormEvent } from 'react';

export default function AuditFinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input,textarea,select');
    fields.forEach((f) => {
      if (f.name) data.append(f.name, f.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d6t8ae0q778icnc8uk10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="final-cta" className="py-16 md:py-24 bg-[#1A2B4C] relative overflow-hidden">
      {/* Decorative rings — hidden on mobile */}
      <div className="hidden md:block absolute top-0 right-0 w-80 h-80 border border-[#C5A880]/10 rounded-full translate-x-1/3 -translate-y-1/3"></div>
      <div className="hidden md:block absolute bottom-0 left-0 w-96 h-96 border border-[#C5A880]/10 rounded-full -translate-x-1/3 translate-y-1/3"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          {/* Left — info */}
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 bg-[#C5A880]/15 border border-[#C5A880]/30 rounded-full px-4 sm:px-5 py-2 mb-5 md:mb-6">
              <i className="ri-rocket-line text-[#C5A880] text-sm"></i>
              <span className="text-[#C5A880] text-xs sm:text-sm font-semibold font-['Montserrat'] uppercase tracking-widest">Финальный призыв</span>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 md:mb-6 font-['Playfair_Display'] leading-tight">
              Переходите от интуитивных догадок к{' '}
              <span className="text-[#C5A880]">точным маркетинговым данным</span>
            </h2>

            <p className="text-white/70 text-base sm:text-lg leading-relaxed font-['Montserrat'] mb-8 md:mb-10 text-justify">
              Профессиональный аудит веб-сайта — это инвестиция, которая возвращается за счёт роста конверсии уже при первых системных изменениях на сайте.
            </p>

            {/* Benefits */}
            <div className="space-y-3 sm:space-y-4 mb-8 md:mb-10">
              {[
                'Вы получаете PDF-отчёт с конкретными рекомендациями, а не размытыми советами',
                'Анализ 208 параметров охватывает всё — от скорости до логики продаж',
                'Конкурентная разведка по 5 лидерам вашей ниши',
                'Чек-лист 15 критических ошибок в подарок',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#C5A880] rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1A2B4C] text-xs font-bold"></i>
                  </div>
                  <p className="text-white/75 font-['Montserrat'] text-sm leading-relaxed text-justify">{text}</p>
                </div>
              ))}
            </div>

            {/* Price & deadline */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-white/5 border border-[#C5A880]/30 rounded-2xl p-4 sm:p-5 text-center">
                <div className="text-[#C5A880] text-2xl sm:text-3xl font-bold font-['Playfair_Display']">3 000 ₽</div>
                <div className="text-white/60 text-xs sm:text-sm font-['Montserrat'] mt-1">Стоимость аудита</div>
              </div>
              <div className="bg-white/5 border border-[#C5A880]/30 rounded-2xl p-4 sm:p-5 text-center">
                <div className="text-[#C5A880] text-2xl sm:text-3xl font-bold font-['Playfair_Display']">2–3</div>
                <div className="text-white/60 text-xs sm:text-sm font-['Montserrat'] mt-1">Рабочих дня</div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="flex-1 w-full">
            <div className="bg-white rounded-3xl p-5 sm:p-8 border-2 border-[#C5A880]/20">
              {submitted ? (
                <div className="text-center py-10 sm:py-12">
                  <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-[#C5A880]/15 rounded-full mx-auto mb-5">
                    <i className="ri-check-double-line text-[#C5A880] text-3xl sm:text-4xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1A2B4C] mb-3 font-['Playfair_Display']">
                    Заявка принята!
                  </h3>
                  <p className="text-gray-600 font-['Montserrat'] text-sm sm:text-base">
                    Свяжусь с вами в течение нескольких часов для уточнения деталей и оплаты.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg sm:text-xl font-bold text-[#1A2B4C] mb-2 font-['Playfair_Display']">
                    Заказать аудит сайта
                  </h3>
                  <p className="text-gray-500 text-sm font-['Montserrat'] mb-5 sm:mb-6">
                    Заполните форму — отвечу в течение нескольких часов
                  </p>

                  <form
                    id="audit-order-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-3 sm:space-y-4"
                  >
                    <input type="hidden" name="form_type" value="audit_order" />

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Ваше имя <span className="text-[#C5A880]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Как к вам обращаться?"
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Email <span className="text-[#C5A880]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Телефон / Telegram
                      </label>
                      <input
                        type="text"
                        name="phone"
                        placeholder="+7 (___) ___-__-__"
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Адрес сайта <span className="text-[#C5A880]">*</span>
                      </label>
                      <input
                        type="url"
                        name="website_url"
                        required
                        placeholder="https://yoursite.ru"
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Ниша / Сфера бизнеса
                      </label>
                      <input
                        type="text"
                        name="niche"
                        placeholder="Например: продажа мебели, клиника, онлайн-курсы"
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1A2B4C] text-sm font-semibold font-['Montserrat'] mb-1.5">
                        Ваша главная задача
                      </label>
                      <textarea
                        name="task"
                        rows={3}
                        maxLength={500}
                        placeholder="Что вас больше всего беспокоит? Например: мало заявок, высокий отказ, плохая конверсия..."
                        className="w-full border border-gray-200 focus:border-[#C5A880] focus:outline-none rounded-xl px-4 py-3 text-sm font-['Montserrat'] text-gray-800 transition-colors duration-200 resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#C5A880] hover:bg-[#B39770] text-[#1A2B4C] py-4 rounded-xl font-bold font-['Montserrat'] transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {loading ? 'Отправляю...' : 'Заказать профессиональный аудит'}
                    </button>

                    <p className="text-gray-400 text-xs text-center font-['Montserrat']">
                      Нажимая кнопку, вы соглашаетесь с обработкой персональных данных
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
